import { useState } from "react";
import { Bath, Car, Droplets, Flame, Play, Star, Trees, Utensils, Waves, Wifi, Tag } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { mediaUrl } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const ICONS = { waves: Waves, flame: Flame, bath: Bath, car: Car, utensils: Utensils, droplets: Droplets, trees: Trees, wifi: Wifi };
const isYoutube = (u) => /youtube\.com|youtu\.be/.test(u || "");
const ytEmbed = (u) => {
  const m = u.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : u;
};

function SectionHead({ eyebrow, title, text }) {
  return (
    <div className="max-w-2xl">
      <p className="eyebrow">{eyebrow}</p>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">{title}</h2>
      {text && <p className="mt-4 text-muted-foreground text-base md:text-lg">{text}</p>}
    </div>
  );
}

export function Gallery({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <section id="galeria" className="py-16 sm:py-24 bg-[#F3EDE2]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Galería" title="Fotos y videos de Rachel Lodge" />
        <div className="mt-10 columns-2 md:columns-3 lg:columns-4 gap-4 [&>*]:mb-4">
          {items.map((g, i) => (
            <button key={g.id || i} onClick={() => setOpen(g)} data-testid={`gallery-item-${i}`}
              className="relative block w-full overflow-hidden rounded-2xl group break-inside-avoid">
              {g.type === "video" ? (
                isYoutube(g.url) ? <div className="aspect-video bg-[#1F2421] grid place-items-center text-white"><Play className="h-10 w-10" /></div>
                  : <video src={mediaUrl(g.url)} muted playsInline className="w-full" />
              ) : <img src={mediaUrl(g.url)} alt={g.caption || "Rachel Lodge"} loading="lazy" className="w-full transition-transform duration-700 group-hover:scale-105" />}
              {g.type === "video" && <span className="absolute top-3 right-3 rounded-full bg-black/50 text-white p-2"><Play className="h-4 w-4" /></span>}
              {g.caption && <span className="absolute bottom-0 inset-x-0 p-3 text-left text-xs text-white bg-gradient-to-t from-black/60">{g.caption}</span>}
            </button>
          ))}
        </div>
      </div>
      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent className="max-w-5xl p-2 bg-[#1F2421] border-0" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">Galería</DialogTitle>
          {open && (open.type === "video" ? (
            isYoutube(open.url) ? <iframe title="video" src={ytEmbed(open.url)} className="w-full aspect-video rounded-xl" allowFullScreen />
              : <video src={mediaUrl(open.url)} controls autoPlay className="w-full max-h-[80vh] rounded-xl" />
          ) : <img src={mediaUrl(open.url)} alt={open.caption || ""} className="w-full max-h-[80vh] object-contain rounded-xl" />)}
        </DialogContent>
      </Dialog>
    </section>
  );
}

export function Amenities({ content }) {
  return (
    <section id="instalaciones" className="py-16 sm:py-24" data-testid="amenities-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <SectionHead eyebrow="Instalaciones y servicios" title={content.about_title} text={content.about_text} />
        </div>
        <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4">
          {(content.amenities || []).map((a, i) => {
            const Icon = ICONS[a.icon] || Trees;
            return (
              <div key={a.id || i} className="rounded-2xl border border-border bg-white p-6 transition-colors hover:bg-[#E9EFE6]" data-testid={`amenity-${i}`}>
                <Icon className="h-6 w-6 text-[#5E6B52]" />
                <h3 className="mt-4 font-serif text-xl">{a.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{a.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function Reviews({ reviews }) {
  if (!reviews?.length) return null;
  return (
    <section className="py-16 sm:py-24 bg-[#5E6B52] text-[#FAF7F2]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="eyebrow !text-[#E5DCCB]">Opiniones</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-light">Lo que dicen nuestros visitantes</h2>
        <div className="mt-10 flex gap-5 overflow-x-auto snap-x pb-4">
          {reviews.map((r, i) => (
            <figure key={r.id || i} className="snap-start shrink-0 w-[85%] sm:w-[420px] rounded-3xl bg-[#4A5540] p-7" data-testid={`review-${i}`}>
              <div className="flex gap-1">{Array.from({ length: Number(r.rating) || 5 }).map((_, k) => <Star key={k} className="h-4 w-4 fill-[#E5DCCB] text-[#E5DCCB]" />)}</div>
              <blockquote className="mt-4 font-serif text-xl leading-snug">“{r.text}”</blockquote>
              <figcaption className="mt-5 text-sm text-[#E5DCCB]">{r.name}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Promotions({ promotions, onBook }) {
  if (!promotions?.length) return null;
  return (
    <section className="py-16 sm:py-20" data-testid="promotions-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Promociones" title="Ofertas vigentes" />
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {promotions.map((p, i) => (
            <div key={p.id || i} className="rounded-3xl border border-[#C86D51]/30 bg-[#C86D51]/5 p-7" data-testid={`promotion-${i}`}>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C86D51] text-white px-3 py-1 text-xs font-semibold">
                <Tag className="h-3.5 w-3.5" />{p.discount_percent ? `${p.discount_percent}% dcto.` : "Promoción"}
              </span>
              <h3 className="mt-4 font-serif text-2xl">{p.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>
              {(p.start || p.end) && <p className="mt-3 text-xs text-muted-foreground">Válida {p.start ? `desde ${fmtDate(p.start)}` : ""} {p.end ? `hasta ${fmtDate(p.end)}` : ""}</p>}
              <button onClick={onBook} className="mt-5 text-sm font-semibold text-[#C86D51] hover:underline">Reservar →</button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Faq({ faqs, policies }) {
  return (
    <section id="preguntas" className="py-16 sm:py-24 bg-[#F3EDE2]" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
        <div>
          <SectionHead eyebrow="Preguntas frecuentes" title="Todo lo que necesitas saber" />
          <Accordion type="single" collapsible className="mt-8">
            {(faqs || []).map((f, i) => (
              <AccordionItem key={f.id || i} value={`f${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-serif text-lg">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        <div id="politicas" className="rounded-3xl bg-white border border-border p-7 sm:p-9 self-start" data-testid="policies-section">
          <p className="eyebrow">Políticas de reserva y cancelación</p>
          <p className="mt-4 leading-relaxed whitespace-pre-line">{policies?.text}</p>
          {policies?.change_deadline_days > 0 && (
            <p className="mt-4 text-sm text-muted-foreground">Las solicitudes de cambio de fecha deben realizarse con al menos {policies.change_deadline_days} días de anticipación.</p>
          )}
          <p className="mt-4 text-sm text-muted-foreground whitespace-pre-line">{policies?.privacy_text}</p>
        </div>
      </div>
    </section>
  );
}
